import "./LabAddComponent.scss";
import Grid from "@mui/material/Grid";
import TimelineComponent from "../../../shared/components/timeline/timelineComponent";
import {ImageConfig} from "../../../constants";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ITimelineItem} from "../../../shared/models/timeline.model";
import _ from "lodash";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {CommonService} from "../../../shared/services";
import LabDirectorDetailsAddComponent from "./lab-director-details-add/LabDirectorDetailsAddComponent";
import LabInformationDetailsAddComponent from "./lab-information-details-add/LabInformationDetailsAddComponent";
import LabAttachmentsAddComponent from "./lab-attachments-add/LabAttachmentsAddComponent";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {useDispatch} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import LabAdminDetailsAddComponent from "./lab-admin-details-add/LabAdminDetailsAddComponent";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import LabReportSetupComponent from "./lab-report-setup/LabReportSetupComponent";
import LabTestAndPanelsAddComponent from "./lab-test-and-panels-add/LabTestAndPanelsAddComponent";
import LabUserAddComponent from "./lab-user-add/LabUserAddComponent";
import {LabRoleType} from "../../../shared/models/lab.model";

interface LabAddComponentProps {

}

const LAB_DETAILS = 'lab_details';
const LAB_ATTACHMENTS = 'lab_attachments';
const DIRECTOR_DETAILS = 'director_details';
const ADMIN_DETAILS = 'admin_details';
const USER_DETAILS = 'user_details';
const TEST_AND_PANELS = 'test_and_panels';
const REPORT_SETUP = 'report_Setup';

const sendLabSteps = [LAB_DETAILS, ADMIN_DETAILS, LAB_ATTACHMENTS];
const sendReceiveLabSteps = [LAB_DETAILS, ADMIN_DETAILS, DIRECTOR_DETAILS, TEST_AND_PANELS, LAB_ATTACHMENTS, REPORT_SETUP];

export const GENERIC_LAB_ADD_STEPS: ITimelineItem[] = [
    {
        id: LAB_DETAILS,
        title: "Lab Details",
    },
    {
        id: ADMIN_DETAILS,
        title: "Admin Details",
    },
    {
        id: USER_DETAILS,
        title: "User Details"
    },
    {
        id: DIRECTOR_DETAILS,
        title: "Director Details",
    },
    {
        id: TEST_AND_PANELS,
        title: "Test and Panels",
    },
    {
        id: LAB_ATTACHMENTS,
        title: "Attachments",
    },
    {
        id: REPORT_SETUP,
        title: "Report Setup",
    }
];
const labRoles = CommonService._staticData.labRoles;
const LabAddComponent = (props: LabAddComponentProps) => {
    const {labRole} = useParams<{ labRole?: LabRoleType }>();
    const STEPS = useMemo(() => {
        if (labRole === 'sendLab') {
            return GENERIC_LAB_ADD_STEPS.filter(step => sendLabSteps.includes(step.id));
        } else if (labRole === 'sendReceiveLab' || labRole === 'receiveLab') {
            return GENERIC_LAB_ADD_STEPS.filter(step => sendReceiveLabSteps.includes(step.id));
        } else {
            return []; // Default case when labRole is undefined or does not match
        }
    }, [labRole]);
    const [steps] = useState<ITimelineItem[]>([..._.cloneDeep(STEPS)]);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState<ITimelineItem | undefined>(STEPS[0]);
    const [activeStep, setActiveStep] = React.useState(0);
    const [labID, setLabID] = useState<number | undefined>(undefined);
    const [labDetails, setLabDetails] = useState<any>(undefined);
    const [isLabDetailsLoading, setIsLabDetailsLoading] = useState<boolean>(false);
    const dispatch = useDispatch();
    const [selectedTestsAndPanels, setSelectedTestsAndPanels] = useState<any[]>([]);

    useEffect(() => {
        dispatch(setCurrentNavParams("", "Lab"));
        dispatch(setSideMenuView('compact'));
    }, [dispatch]);

    useEffect(() => {
        const isValidLabRole = CommonService.isValidLabRole(labRole, labRoles);
        if (!isValidLabRole) {
            navigate(CommonService._routeConfig.NotFoundRoute())
        }
    }, [labRole, navigate]);

    const getStepIndexById = useCallback((id: string): number => {
        return STEPS.findIndex(step => step.id === id);
    }, [STEPS]);

    const getLabDetails = useCallback(() => {
        const payload = {};
        let labDetails: any = {};
        setIsLabDetailsLoading(true);
        CommonService._lab.getLabDetails(labID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    labDetails = response.data;
                }
                setLabDetails(labDetails);
                setIsLabDetailsLoading(false);
            })
            .catch((error: any) => {
                setIsLabDetailsLoading(false);
                setLabDetails(labDetails);
            });
    }, [labID]);

    useEffect(() => {
        if (labID) {
            getLabDetails()
        }
    }, [labID, getLabDetails])

    const updateSteps = useCallback((currentStepIndex: number) => {
        if (currentStepIndex === STEPS.length) {
            //prevent to update the steps
            return;
        }
        setCurrentStep(STEPS[currentStepIndex]);
        searchParams.set("step", STEPS[currentStepIndex].id);
        setSearchParams(searchParams);
        setActiveStep(currentStepIndex);
    }, [STEPS, searchParams, setSearchParams]);

    const handlePrevious = useCallback((currentStepIndex: number, currentStepId: string) => {
        updateSteps(currentStepIndex);
    }, [updateSteps]);

    const handleADDSuccess = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Congratulations!',
            confirmationSubTitle: 'Lab has been successfully created.',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.CongratulationImage}`,
            direction: "up",
            hideNoOption: true,
            yes: {
                text: "Proceed",
                color: "primary"
            }

        }).then(() => {
            navigate(CommonService._routeConfig.LabListRoute());
        })
            .catch(() => {
            });
    }, [navigate]);

    /**
     * This function helps to activate the labs on last step
     */
    const updateLabStatus = useCallback(() => {
        const payload = {
            labId: labID,
            isCompleted: true,
            labRole: labDetails?.labRole
        }
        return CommonService._lab.updateLabRoleStatus(payload).then((response) => {
            handleADDSuccess();
            CommonService._alert.showToast(response.message || "Lab created successfully", 'success')
            return response;
        }).catch((error): any => {
            CommonService._alert.showToast(error.error || "Unable to create lab", 'error')
            throw error;
        })
    }, [handleADDSuccess, labDetails?.labRole, labID])

    const handleNext = useCallback((currentStepIndex: number, currentStepId: string, labId?: number, data?: any) => {
        if (parseInt(searchParams.get("lastCompletedStep") || "1") <= currentStepIndex) {
            searchParams.set("lastCompletedStep", currentStepIndex.toString());
            setSearchParams(searchParams);
        }
        switch (currentStepId) {
            case LAB_DETAILS :
                if (labId) {
                    setLabID(labId);
                    searchParams.set("labId", labId.toString());
                    setSearchParams(searchParams);
                }
                if (data) {
                    setLabDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            ...data
                        }
                    });
                }
                updateSteps(currentStepIndex);
                break;
            case ADMIN_DETAILS:
                if (data) {
                    setLabDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            ...data
                        }
                    });
                }
                updateSteps(currentStepIndex);
                break;
            case USER_DETAILS:
                if (data) {
                    setLabDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            userDetails: data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case DIRECTOR_DETAILS:
                if (data) {
                    setLabDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            ...data
                        }
                    });
                }
                updateSteps(currentStepIndex);
                break;
            case TEST_AND_PANELS:
                if (data) {
                    setSelectedTestsAndPanels(data);
                }
                updateSteps(currentStepIndex);
                break;
            case LAB_ATTACHMENTS :
                updateSteps(currentStepIndex);
                break;
            case REPORT_SETUP :
                // handleADDSuccess()
                //moved handleADDSuccess function to is last step if condition block
                break;
            default:
                <></>
        }
    }, [searchParams, setSearchParams, updateSteps]);

    useEffect(() => {
        const step = searchParams.get("step");
        setActiveStep(STEPS.findIndex((stepItem: any) => stepItem.id === step));
        const labId = searchParams.get("labId");
        const stepIDS = STEPS.map(item => item.id);
        let stepIndex = 0;
        if (labId) {
            setLabID(parseInt(labId, 10));
            if (stepIDS.indexOf(step) > -1) {
                stepIndex = stepIDS.indexOf(step);
            } else {
                stepIndex = 1;
            }
        } else {
            stepIndex = 0;
            setActiveStep(0)
        }
        setCurrentStep(STEPS[stepIndex]);
    }, [STEPS, searchParams]);

    const handleLabCancel = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to exit without adding this lab?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: "Exit",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        }).then(() => {
            navigate(CommonService._routeConfig.LabListRoute());
        }).catch(() => {
            // do nothing;
        });
    }, [navigate]);

    const renderComponent = useCallback(() => {
        if (!labID) {
            if (currentStep?.id === LAB_DETAILS) {
                return <LabInformationDetailsAddComponent
                    next={(labID: number) => {
                        handleNext(1, LAB_DETAILS, labID)
                    }}
                    cancel={handleLabCancel}
                />
            }
        } else {
            const currentIndex = getStepIndexById(currentStep?.id || STEPS[0]?.id)
            switch (currentStep && currentStep.id) {
                case LAB_DETAILS:
                    return <LabInformationDetailsAddComponent
                        labID={labID}
                        labDetails={labDetails}
                        cancel={handleLabCancel}
                        next={(data: number) => handleNext(currentIndex + 1, LAB_DETAILS, undefined, data)}
                    />
                case ADMIN_DETAILS:
                    return <LabAdminDetailsAddComponent
                        labID={labID}
                        labDetails={labDetails}
                        cancel={handleLabCancel}
                        next={(data: number) => handleNext(currentIndex + 1, ADMIN_DETAILS, undefined, data)}
                        prev={() => handlePrevious(currentIndex - 1, ADMIN_DETAILS)}
                    />
                case USER_DETAILS:
                    return <LabUserAddComponent
                        labID={labID}
                        labDetails={labDetails}
                        cancel={handleLabCancel}
                        next={(data: number) => handleNext(currentIndex + 1, USER_DETAILS, undefined, data)}
                        prev={() => handlePrevious(currentIndex - 1, USER_DETAILS)}
                    />
                case DIRECTOR_DETAILS:
                    return <LabDirectorDetailsAddComponent
                        labID={labID}
                        labDetails={labDetails}
                        cancel={handleLabCancel}
                        next={(data: number) => handleNext(currentIndex + 1, DIRECTOR_DETAILS, undefined, data)}
                        prev={() => handlePrevious(currentIndex - 1, DIRECTOR_DETAILS)}
                    />
                case TEST_AND_PANELS:
                    return <LabTestAndPanelsAddComponent
                        labID={labID}
                        cancel={handleLabCancel}
                        selectedTestsAndPanels={selectedTestsAndPanels}
                        next={(data: number) => handleNext(currentIndex + 1, TEST_AND_PANELS, undefined, data)}
                        prev={() => handlePrevious(currentIndex - 1, LAB_ATTACHMENTS)}
                    />
                case LAB_ATTACHMENTS:
                    return <LabAttachmentsAddComponent
                        labID={labID}
                        labDetails={labDetails}
                        cancel={handleLabCancel}
                        updateLabStatus={(currentIndex === STEPS.length - 1) ? updateLabStatus : undefined}
                        next={(data: number) => handleNext(currentIndex + 1, LAB_ATTACHMENTS, undefined, data)}
                        prev={() => handlePrevious(currentIndex - 1, LAB_ATTACHMENTS)}
                    />
                case REPORT_SETUP:
                    return <LabReportSetupComponent
                        labID={labID}
                        labDetails={labDetails}
                        cancel={handleLabCancel}
                        updateLabStatus={(currentIndex === STEPS.length - 1) ? updateLabStatus : undefined}
                        next={() => handleNext(currentIndex + 1, REPORT_SETUP)}
                        prev={() => handlePrevious(currentIndex - 1, REPORT_SETUP)}
                    />
                default:
                    <></>
            }
        }
    }, [labID, currentStep, handleLabCancel, handleNext, getStepIndexById, STEPS, labDetails, selectedTestsAndPanels, updateLabStatus, handlePrevious]);

    const handleStepClick = useCallback((step: any) => {
        const lastCompletedStep = parseInt(searchParams.get("lastCompletedStep") || '0');
        const clickedStepIndex = STEPS.findIndex((stepItem: any) => stepItem.id === step.id);
        if (clickedStepIndex <= (lastCompletedStep)) {
            setActiveStep(clickedStepIndex);
            setCurrentStep(step);
            searchParams.set("step", step.id);
            setSearchParams(searchParams);
        }
    }, [STEPS, searchParams, setSearchParams]);

    return (
        <div className="lab-creation-layout-screen add-layout-with-timeline screen ">
            <Grid container spacing={2} className="add-form-timeline-wrapper">
                <Grid item sm={12} md={3} xl={3} className="timeline-layout"
                      sx={{
                          display: {xs: "none", md: "block"}
                      }}>
                    <div className="add-layout-title">Adding Lab</div>
                    <div className="add-layout-timeline">
                        <TimelineComponent
                            activeStep={activeStep}
                            steps={steps}
                            handleStepClick={handleStepClick}
                            lastCompletedStep={parseInt(searchParams.get("lastCompletedStep") || '0')}
                        />
                    </div>
                </Grid>
                <Grid item xs={12} sm={12} md={9} xl={9} className="add-form-content">
                    <div className="add-layout-form-content">
                        {renderComponent()}
                    </div>
                </Grid>
            </Grid>
            {isLabDetailsLoading &&
                <div className={"glass-effect h-v-center flex-direction-column"}>
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
        </div>
    );

};

export default LabAddComponent;
